// catalogSlice — Redux slice for the Data Surface Catalog.
// Surfaces are indexed by composite key: `${tenantId}::${surfaceId}`.

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  CatalogSurface,
  SurfaceFreshness,
  AvailableShape,
  ActionSurface,
  HydrationPoint,
  ConnectionStatus,
} from "../../types/index.ts";
import type { RootState } from "../index.ts";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function surfaceKey(tenantId: string, surfaceId: string): string {
  return `${tenantId}::${surfaceId}`;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface CatalogState {
  surfaces: Record<string, CatalogSurface>;
  connectionStatus: ConnectionStatus;
  lastConnectionChangeAt: string | null;
  catchUpInProgress: boolean;
}

const initialState: CatalogState = {
  surfaces: {},
  connectionStatus: "connected",
  lastConnectionChangeAt: null,
  catchUpInProgress: false,
};

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const catalogSlice = createSlice({
  name: "catalog",
  initialState,
  reducers: {
    materializeSurface(state, action: PayloadAction<CatalogSurface>) {
      const surface = action.payload;
      const key = surfaceKey(surface.tenantId, surface.surfaceId);
      state.surfaces[key] = surface;
    },

    updateSurface(
      state,
      action: PayloadAction<{
        tenantId: string;
        surfaceId: string;
        updates: Partial<
          Pick<CatalogSurface, "name" | "description" | "tags" | "shapes" | "actions" | "hydrationPoints" | "ownership">
        >;
      }>,
    ) {
      const { tenantId, surfaceId, updates } = action.payload;
      const key = surfaceKey(tenantId, surfaceId);
      const existing = state.surfaces[key];
      if (!existing) return;
      Object.assign(existing, updates, { updatedAt: new Date().toISOString() });
    },

    markSurfaceStale(
      state,
      action: PayloadAction<{ tenantId: string; surfaceId: string; staleSince: string }>,
    ) {
      const { tenantId, surfaceId, staleSince } = action.payload;
      const key = surfaceKey(tenantId, surfaceId);
      const existing = state.surfaces[key];
      if (!existing) return;
      existing.freshness.isStale = true;
      existing.freshness.staleSince = staleSince;
    },

    refreshSurface(
      state,
      action: PayloadAction<{
        tenantId: string;
        surfaceId: string;
        freshness: SurfaceFreshness;
      }>,
    ) {
      const { tenantId, surfaceId, freshness } = action.payload;
      const key = surfaceKey(tenantId, surfaceId);
      const existing = state.surfaces[key];
      if (!existing) return;
      existing.freshness = freshness;
      existing.updatedAt = freshness.lastUpdatedAt;
    },

    removeSurface(
      state,
      action: PayloadAction<{ tenantId: string; surfaceId: string }>,
    ) {
      const { tenantId, surfaceId } = action.payload;
      const key = surfaceKey(tenantId, surfaceId);
      delete state.surfaces[key];
    },

    addShapeToSurface(
      state,
      action: PayloadAction<{ tenantId: string; surfaceId: string; shape: AvailableShape }>,
    ) {
      const { tenantId, surfaceId, shape } = action.payload;
      const key = surfaceKey(tenantId, surfaceId);
      const existing = state.surfaces[key];
      if (!existing) return;
      const idx = existing.shapes.findIndex((s) => s.shapeId === shape.shapeId);
      if (idx >= 0) {
        existing.shapes[idx] = shape;
      } else {
        existing.shapes.push(shape);
      }
    },

    addActionToSurface(
      state,
      action: PayloadAction<{ tenantId: string; surfaceId: string; actionSurface: ActionSurface }>,
    ) {
      const { tenantId, surfaceId, actionSurface } = action.payload;
      const key = surfaceKey(tenantId, surfaceId);
      const existing = state.surfaces[key];
      if (!existing) return;
      const idx = existing.actions.findIndex((a) => a.actionId === actionSurface.actionId);
      if (idx >= 0) {
        existing.actions[idx] = actionSurface;
      } else {
        existing.actions.push(actionSurface);
      }
    },

    addHydrationPoint(
      state,
      action: PayloadAction<{ tenantId: string; surfaceId: string; hydrationPoint: HydrationPoint }>,
    ) {
      const { tenantId, surfaceId, hydrationPoint } = action.payload;
      const key = surfaceKey(tenantId, surfaceId);
      const existing = state.surfaces[key];
      if (!existing) return;
      const idx = existing.hydrationPoints.findIndex((h) => h.hydrationId === hydrationPoint.hydrationId);
      if (idx >= 0) {
        existing.hydrationPoints[idx] = hydrationPoint;
      } else {
        existing.hydrationPoints.push(hydrationPoint);
      }
    },

    connectionLost(state, action: PayloadAction<{ at: string }>) {
      state.connectionStatus = "disconnected";
      state.lastConnectionChangeAt = action.payload.at;
    },

    connectionRestored(state, action: PayloadAction<{ at: string }>) {
      state.connectionStatus = "reconnecting";
      state.lastConnectionChangeAt = action.payload.at;
      state.catchUpInProgress = true;
    },

    catchUpCompleted(state) {
      state.connectionStatus = "connected";
      state.catchUpInProgress = false;
    },
  },
});

export const {
  materializeSurface,
  updateSurface,
  markSurfaceStale,
  refreshSurface,
  removeSurface,
  addShapeToSurface,
  addActionToSurface,
  addHydrationPoint,
  connectionLost,
  connectionRestored,
  catchUpCompleted,
} = catalogSlice.actions;

export default catalogSlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectAllSurfaces = (state: RootState): CatalogSurface[] =>
  Object.values(state.catalog.surfaces);

export const selectSurfaceByKey = (
  state: RootState,
  tenantId: string,
  surfaceId: string,
): CatalogSurface | undefined =>
  state.catalog.surfaces[surfaceKey(tenantId, surfaceId)];

export const selectSurfacesByTenant = (state: RootState, tenantId: string): CatalogSurface[] =>
  Object.values(state.catalog.surfaces).filter((s) => s.tenantId === tenantId);

export const selectSurfacesByProduct = (state: RootState, product: string): CatalogSurface[] =>
  Object.values(state.catalog.surfaces).filter((s) => s.productSource === product);

export const selectSurfacesByContext = (state: RootState, contextId: string): CatalogSurface[] =>
  Object.values(state.catalog.surfaces).filter((s) => s.ownership.contextId === contextId);

export const selectStaleSurfaces = (state: RootState): CatalogSurface[] =>
  Object.values(state.catalog.surfaces).filter((s) => s.freshness.isStale);

export const selectFreshSurfaces = (state: RootState): CatalogSurface[] =>
  Object.values(state.catalog.surfaces).filter((s) => !s.freshness.isStale);

export const selectConnectionStatus = (state: RootState): ConnectionStatus =>
  state.catalog.connectionStatus;

export const selectCatchUpInProgress = (state: RootState): boolean =>
  state.catalog.catchUpInProgress;

export const selectSurfaceCount = (state: RootState): number =>
  Object.keys(state.catalog.surfaces).length;

export const selectContextIds = (state: RootState): string[] => {
  const ids = new Set<string>();
  for (const s of Object.values(state.catalog.surfaces)) {
    ids.add(s.ownership.contextId);
  }
  return Array.from(ids);
};

export const selectSurfacesByTag = (state: RootState, tag: string): CatalogSurface[] =>
  Object.values(state.catalog.surfaces).filter((s) => s.tags.includes(tag));
